import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import {
  DOCUMENTS_EVENTS,
  DOCUMENTS_PROPERTIES,
} from '@/constants/analytics/documents-events';
import { usePostHogCapture } from '@/hooks/usePostHogCapture';
import { cn } from '@/lib/utils';
import {
  expandDocument,
  PinnedDocument,
  selectExpandedDocId,
  selectPinnedDocs,
  unpinDocument,
} from '@/services/slices/pinnedDocsSlice';

import { DockTab } from './DockTab';

export const DockTabBar = () => {
  const dispatch = useDispatch();
  const capture = usePostHogCapture();
  const pinnedDocs = useSelector(selectPinnedDocs);
  const expandedDocId = useSelector(selectExpandedDocId);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check scroll state
  const checkScrollState = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(
      container.scrollLeft < container.scrollWidth - container.clientWidth - 1,
    );
  }, []);

  useEffect(() => {
    checkScrollState();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollState);
      window.addEventListener('resize', checkScrollState);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', checkScrollState);
      }
      window.removeEventListener('resize', checkScrollState);
    };
  }, [checkScrollState, pinnedDocs]);

  // Scroll to active tab
  useEffect(() => {
    if (expandedDocId && scrollContainerRef.current) {
      const activeTab = scrollContainerRef.current.querySelector(
        `[data-doc-id="${expandedDocId}"]`,
      );
      if (activeTab) {
        activeTab.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      }
    }
  }, [expandedDocId]);

  const handleScroll = (direction: 'left' | 'right') => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = 200;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleTabClick = (doc: PinnedDocument) => {
    dispatch(expandDocument(doc.id));

    // Track tab click
    capture(DOCUMENTS_EVENTS.PINNED_DOCK_TAB_CLICKED, {
      [DOCUMENTS_PROPERTIES.PAGE]: 'global',
      [DOCUMENTS_PROPERTIES.COMPONENT]: 'pinned_dock',
      [DOCUMENTS_PROPERTIES.MATTER_ID]: doc.matterId,
      [DOCUMENTS_PROPERTIES.DOCUMENT_ID]: doc.id,
      [DOCUMENTS_PROPERTIES.FILE_NAME]: doc.fileName,
      [DOCUMENTS_PROPERTIES.FILE_TYPE]: doc.fileType,
    });
  };

  const handleTabClose = (doc: PinnedDocument) => {
    dispatch(unpinDocument(doc.id));

    // Track unpin via dock tab close
    capture(DOCUMENTS_EVENTS.DOCUMENT_UNPINNED, {
      [DOCUMENTS_PROPERTIES.PAGE]: 'global',
      [DOCUMENTS_PROPERTIES.COMPONENT]: 'pinned_dock',
      [DOCUMENTS_PROPERTIES.MATTER_ID]: doc.matterId,
      [DOCUMENTS_PROPERTIES.DOCUMENT_ID]: doc.id,
      [DOCUMENTS_PROPERTIES.FILE_NAME]: doc.fileName,
      [DOCUMENTS_PROPERTIES.FILE_TYPE]: doc.fileType,
      [DOCUMENTS_PROPERTIES.UNPIN_SOURCE]: 'dock_tab_close',
      [DOCUMENTS_PROPERTIES.PINNED_COUNT]: pinnedDocs.length - 1,
    });
  };

  if (pinnedDocs.length === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 flex items-end justify-center pointer-events-none">
      <div className="flex items-end pointer-events-auto">
        {/* Left scroll button */}
        {canScrollLeft && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleScroll('left')}
            className="h-7 w-6 p-0 rounded-none rounded-tl-md bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border-t border-l border-zinc-700"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>
        )}

        {/* Tabs container */}
        <div
          ref={scrollContainerRef}
          className={cn(
            'flex items-end gap-0.5 overflow-x-auto scrollbar-hide max-w-[calc(100vw-200px)]',
            'bg-zinc-900 px-1 pt-1',
            !canScrollLeft && 'rounded-tl-md',
            !canScrollRight && 'rounded-tr-md',
          )}
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {pinnedDocs.map((doc) => (
            <div key={doc.id} data-doc-id={doc.id}>
              <DockTab
                doc={doc}
                isActive={expandedDocId === doc.id}
                onClick={() => handleTabClick(doc)}
                onClose={() => handleTabClose(doc)}
              />
            </div>
          ))}
        </div>

        {/* Right scroll button */}
        {canScrollRight && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleScroll('right')}
            className="h-7 w-6 p-0 rounded-none rounded-tr-md bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border-t border-r border-zinc-700"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default DockTabBar;
